import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

// ============================================================================
// useLocationNotesSummary — calls the summarize-location Edge Function and
// caches the result in React Query.
//
// The Edge Function itself caches by source-content hash, so most calls
// after the first per-(custno, locno) are nearly instant. The 24-hour
// React Query staleTime prevents re-invocation within a single browser
// session.
//
// See sedgwick-crm/edge-functions/summarize-location/index.ts (deployed
// to Supabase, source preserved in C:\ESCBACKUP\edge-functions\) for the
// server-side flow.
// ============================================================================

export interface LocationNotesSummaryResult {
  summary: string;
  cached: boolean;
  generated_at: string;
}

export function useLocationNotesSummary(
  custno: string | undefined,
  locno: string | undefined,
) {
  return useQuery({
    queryKey: ["location_notes_summary", custno, locno],
    queryFn: async (): Promise<LocationNotesSummaryResult | null> => {
      if (!custno || !locno) return null;
      const { data, error } = await supabase.functions.invoke(
        "summarize-location",
        { body: { custno, locno } },
      );
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data as LocationNotesSummaryResult;
    },
    enabled: !!custno && !!locno,
    staleTime: 24 * 60 * 60 * 1000,
    retry: 1,
  });
}
